import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarDays, MapPin, Clock, Repeat, ChevronRight, ExternalLink, Image as ImageIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { PublicHeader } from "@/components/PublicHeader";
import { EventDetailModal } from "@/components/EventDetailModal";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";
import { fmtDate, pickLang, fmtTime12 } from "@/lib/format";

export default function EventsPage() {
  const { t, lang } = useI18n();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/events");
        if (!cancelled) setEvents(data);
      } catch (err) {
        if (!cancelled) console.error("EventsPage: failed to load events", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div>
      <PublicHeader />
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        className="px-4 pt-4 space-y-4"
      >
        <div className="flex items-center gap-2 px-1">
          <CalendarDays className="h-5 w-5 text-primary" />
          <h1 className="font-serif-display text-2xl font-semibold tracking-tight">{t.events.title}</h1>
        </div>

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-2xl" />
            ))}
          </div>
        ) : events.length === 0 ? (
          <Card className="rounded-2xl border-dashed bg-white/60">
            <CardContent className="py-12 text-center text-sm text-muted-foreground" data-testid="events-empty">
              {t.events.noEvents}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="events-list">
            {events.map((ev) => {
              const title = pickLang(ev, "title", lang);
              const desc = pickLang(ev, "description", lang);
              const hasFlyer = !!(ev.flyer_url || ev.image_url);
              const hasReg = !!(ev.registration_url || "").trim();
              return (
                <button
                  key={ev.id}
                  type="button"
                  onClick={() => setSelected(ev)}
                  className="group block w-full text-left rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 transition-transform active:scale-[0.99]"
                  data-testid={`event-card-${ev.id}`}
                  aria-label={`${title} - ${t.events.tapForDetails}`}
                >
                  <Card className="relative overflow-hidden rounded-2xl shadow-sm ring-1 ring-black/5 before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-primary group-hover:shadow-md transition-shadow">
                    <CardContent className="p-4 pl-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-serif-display text-lg font-semibold leading-tight">{title}</p>
                            {ev.recurring_weekly && (
                              <Badge variant="secondary" className="gap-1 text-[10px]" data-testid={`event-recurring-${ev.id}`}>
                                <Repeat className="h-3 w-3" />
                                {t.admin?.eventsForm?.recurringBadge || (lang === "es" ? "Cada semana" : "Weekly")}
                              </Badge>
                            )}
                            {hasFlyer && (
                              <Badge variant="outline" className="gap-1 text-[10px]" data-testid={`event-has-flyer-${ev.id}`}>
                                <ImageIcon className="h-3 w-3" />
                                Flyer
                              </Badge>
                            )}
                            {hasReg && (
                              <Badge className="gap-1 text-[10px] bg-primary/15 text-primary hover:bg-primary/20" data-testid={`event-has-registration-${ev.id}`}>
                                <ExternalLink className="h-3 w-3" />
                                {t.events.registerCta}
                              </Badge>
                            )}
                          </div>
                          {desc && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{desc}</p>}
                          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                            <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{fmtDate(ev.event_date, lang)}</span>
                            {ev.event_time && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{fmtTime12(ev.event_time)}</span>}
                            {ev.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{ev.location}</span>}
                          </div>
                        </div>
                        <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                      </div>
                    </CardContent>
                  </Card>
                </button>
              );
            })}
          </div>
        )}
        <div className="h-2" />
      </motion.div>

      <EventDetailModal ev={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
